// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

import { openOverlay } from './overlay';
import { escapeHtml, number, percent } from './format';
import type { Meta } from './types';

export function openAbout(meta: Meta): void {
  const { body } = openOverlay({ variant: 'modal', title: 'About Air Episodes' });

  const sources = Object.values(meta.sources)
    .map(
      (source) =>
        `<li><a href="${source.url}" target="_blank" rel="noopener">${escapeHtml(source.dataset)}</a> — ${escapeHtml(
          source.publisher
        )}. ${escapeHtml(source.attribution)}</li>`
    )
    .join('');

  const gatesPassed = meta.gates.filter((gate) => gate.pass).length;

  body.innerHTML = `
    <h3>What this is</h3>
    <p>Every official summary of Australian air quality is an average. An annual average is an
    average of about 8,760 hours, and the hours that actually harm people are a few dozen of
    them. This site reads the raw hourly record instead, from the government monitoring
    networks of New South Wales, Queensland and the Australian Capital Territory, and puts the
    bad hours in the foreground.</p>
    <p>Across ${number(meta.headline.stationYears)} station-years of reference-grade PM2.5, the
    worst 1% of hours in a year — about 88 of them — carry a median
    <strong>${percent(meta.headline.top1Share)}</strong> of that year's total particle exposure.
    That is the argument of the whole site, and it is not computable from any of the daily or
    annual files the official reports are built on.</p>

    <h3>Where the numbers come from</h3>
    <ul>${sources}</ul>
    <p>Boundaries are ABS ASGS. All four sources are CC BY 4.0. Everything you see is as
    measured: no day has been set aside as an exceptional event, because those determinations
    are published in annual PDF reports rather than in any data feed.</p>

    <h3>How it is put together</h3>
    <p>Readings are grouped into clusters of stations within two kilometres of each other,
    because several monitors sit within metres of one another and would otherwise be able to
    corroborate themselves. An <em>episode</em> is a run of at least
    ${meta.smoke1.minHours} hours in which a cluster exceeded both
    ${meta.smoke1.absoluteFloorUgM3} µg/m³ of PM2.5 — the entire daily standard, inside one hour
    — and its own 95th percentile for that month of the year. That second test is what stops a
    town with heavy winter wood-heater use registering every cold evening as an event.</p>
    <p>An episode that no neighbouring cluster corroborated at its peak hour is published and
    marked <em>suspect</em> rather than deleted. It may be a real event confined to one valley,
    or it may be a faulty instrument, and which of the two it is is worth knowing.</p>

    <h3>What it cannot tell you</h3>
    <ul>
      <li><strong>It is not health advice.</strong> If you have a heart or lung condition, follow
        your written action plan and your doctor, not a web page.</li>
      <li><strong>Only particles are compared across borders.</strong> PM2.5 and PM10 are
        micrograms per cubic metre everywhere. Gases are reported in three different units
        across these three jurisdictions, a hundredfold apart, so comparing them across a border
        would fabricate differences that are not there.</li>
      <li><strong>The window differs by state.</strong> Queensland's hourly record runs
        ${escapeHtml(meta.coverageWindow.QLD.hourly)}; the ACT's
        ${escapeHtml(meta.coverageWindow.ACT.hourly)}; New South Wales
        ${escapeHtml(meta.coverageWindow.NSW.hourly)} hourly, with daily values back to 1994.</li>
      <li><strong>Live readings are provisional.</strong> Roughly one in five recent Queensland
        values is later revised. Live data is drawn separately and never extends a line drawn
        from the validated archive.</li>
      <li><strong>Five jurisdictions are missing</strong>, for five different reasons. The
        Record view names each one.</li>
    </ul>

    <h3>Checks</h3>
    <p>${gatesPassed} of ${meta.gates.length} build-time reconciliation gates passed. They cover
    the things that silently produce a plausible wrong answer here: sentinel values that poison
    an average, station pairs that would corroborate themselves, hour-of-day conversions that
    shift a whole state's daily cycle, and exceedance counts computed over years too incomplete
    to support one. The full list is on the Record view.</p>
    <p>Data built ${escapeHtml(new Date(meta.built).toISOString().slice(0, 10))}.</p>`;
}
